import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useCurrentRefinements, useClearRefinements } from "react-instantsearch";
import FilterPill from "./FilterPill";
import RegionCountryRefinement from "./RegionCountryRefinement";
import useIsMobile from "../hooks/useIsMobile";
import GeoSearchByPlace from "../algolia_search/geo_search_by_place";

// One "Location" pill with two mutually-exclusive modes: pick countries (grouped by continent) or
// search near a place. Switching modes clears the other's selection, so the two never combine —
// which also sidesteps Algolia's inability to OR a geo radius with facet filters.

const LOCATION_ATTRS = ["location.lvl0", "location.lvl1"];

const segButtonStyle = (active) => ({
  flex: 1,
  height: 32,
  border: "none",
  borderRadius: 7,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  background: active ? "#fff" : "transparent",
  color: active ? "#533dd1" : "#6b7280",
  boxShadow: active ? "0 1px 2px rgba(16,24,40,0.08)" : "none",
});

function LocationFilter({ initialLocationName = undefined, initialLocationRadius = undefined, resetSignal = 0 }) {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState(initialLocationName ? "place" : "country");
  const [geoActive, setGeoActive] = useState(!!initialLocationName);
  const [geoLabel, setGeoLabel] = useState(initialLocationName ?? null);
  const [geoResetSignal, setGeoResetSignal] = useState(0);

  const { items } = useCurrentRefinements({ includedAttributes: LOCATION_ATTRS });
  const countryLabels = useMemo(
    () => items.flatMap((i) => i.refinements.map((r) => r.label.split(" > ").pop())),
    [items],
  );

  const { refine: clearCountries } = useClearRefinements({ includedAttributes: LOCATION_ATTRS });
  const clearGeo = () => setGeoResetSignal((s) => s + 1);

  const parentResetRef = useRef(resetSignal);
  useEffect(() => {
    if (resetSignal === parentResetRef.current) return;
    parentResetRef.current = resetSignal;
    clearCountries();
    clearGeo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  const switchMode = (next) => {
    if (next === mode) return;
    if (next === "place") clearCountries();
    else clearGeo();
    setMode(next);
  };

  let active = false;
  let activeText = null;
  if (mode === "place") {
    active = geoActive;
    activeText = geoActive ? geoLabel : null;
  } else if (countryLabels.length === 1) {
    active = true;
    [activeText] = countryLabels;
  } else if (countryLabels.length > 1) {
    active = true;
    activeText = `${countryLabels[0]} +${countryLabels.length - 1}`;
  }

  return (
    <FilterPill label="Location" active={active} activeText={activeText} bodyScroll>
      <div
        style={{
          width: isMobile ? "100%" : "min(300px, calc(100vw - 24px))",
          display: "flex",
          flexDirection: "column",
          flex: isMobile ? 1 : "none",
          minHeight: 0,
          maxHeight: isMobile ? "none" : 400,
        }}
      >
        <div style={{ padding: "12px 12px 8px" }}>
          <div style={{ display: "flex", background: "#f2f3f5", borderRadius: 9, padding: 3, gap: 3 }}>
            <button type="button" style={segButtonStyle(mode === "country")} onClick={() => switchMode("country")}>
              By country
            </button>
            <button type="button" style={segButtonStyle(mode === "place")} onClick={() => switchMode("place")}>
              Near a place
            </button>
          </div>
        </div>
        {/* Both stay mounted (CSS hide) so their Algolia widgets keep state across mode switches. */}
        <div style={{ display: mode === "country" ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <RegionCountryRefinement />
        </div>
        <div style={{ display: mode === "place" ? "block" : "none", padding: "0 12px 12px" }}>
          <GeoSearchByPlace
            initialLocationName={initialLocationName}
            initialLocationRadius={initialLocationRadius}
            onActiveChange={setGeoActive}
            onLabelChange={setGeoLabel}
            resetSignal={geoResetSignal}
          />
        </div>
      </div>
    </FilterPill>
  );
}

LocationFilter.propTypes = {
  initialLocationName: PropTypes.string,
  initialLocationRadius: PropTypes.string,
  resetSignal: PropTypes.number,
};

export default LocationFilter;
